import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Cart, Dish, DietaryTag, Menu } from '../../shared/menu';
import { DISH_LOW_CONFIDENCE_THRESHOLD, MENU_LEVEL_LOW_CONFIDENCE_RATIO, deriveMealMode, dishMinConfidence, isLikelyForDiet } from '../../shared/menu';
import { fetchMenu, deleteOrderRemote } from '../lib/api';
import { loadMenuLocal, saveMenuLocal } from '../lib/persist';
import { migrateCartToNewMenu } from '../lib/migrateMenu';
import { resolveMenuName, useCart, useDismissedAddPageBanners, useMenuNames, useOrderHistory, useSettings } from '../lib/store';
import { DishCard } from '../components/DishCard';
import { DishSheet } from '../components/DishSheet';
import { CartPill } from '../components/CartPill';
import { CuisineCoach } from '../components/CuisineCoach';
import { MenuActionsSheet } from '../components/MenuActionsSheet';
import { MenuPhotoViewer } from '../components/MenuPhotoViewer';
import { NotesBanner } from '../components/NotesBanner';
import { AddPagesFlow } from '../components/AddPagesFlow';
import { useT } from '../lib/i18n';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { Logo } from '../components/Logo';
import { cuisineHintFor, isCuisineHintDismissed } from '../lib/cuisineCoach';
import { resolvePageSrc } from '../lib/resolvePageSrc';

// Most-restrictive dietary flag the user has set, used to surface a single
// "X only" filter pill at the top of the menu. Picking multiple flags is
// supported in storage; the pill picks one to surface, ordered from most
// to least restrictive.
const DIET_FILTER_PRIORITY: DietaryTag[] = [
  'vegan', 'vegetarian', 'halal', 'kosher', 'pescatarian', 'gluten_free', 'dairy_free',
];

const DIET_FILTER_LABELS: Record<DietaryTag, string> = {
  vegan: 'Vegan only',
  vegetarian: 'Vegetarian only',
  pescatarian: 'Pescatarian only',
  halal: 'Halal-friendly only',
  kosher: 'Kosher-friendly only',
  gluten_free: 'Gluten-free only',
  dairy_free: 'Dairy-free only',
};

function primaryDietFilter(flags: DietaryTag[]): DietaryTag | null {
  for (const t of DIET_FILTER_PRIORITY) {
    if (flags.includes(t)) return t;
  }
  return null;
}

export default function MenuScreen() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const filteredAllergens = useSettings((s) => s.hardFilterAllergens);
  const hideUncertainAllergens = useSettings((s) => s.hideUncertainAllergens);
  const dietaryFlags = useSettings((s) => s.dietaryFlags);
  const onboardingChoice = useSettings((s) => s.onboardingChoice);
  const showRevealed = useCart((s) => s.showRevealedAllergens);
  const setShowRevealed = useCart((s) => s.setShowRevealedAllergens);
  const dietFilter = primaryDietFilter(dietaryFlags);
  const [dietFilterActive, setDietFilterActive] = useState(false);
  const setCartMenu = useCart((s) => s.setMenu);
  const cart = useCart((s) => s.cart);
  const addLine = useCart((s) => s.addLine);
  const upsertLine = useCart((s) => s.upsertLine);
  const restoreCart = useCart((s) => s.restoreCart);
  const removeOrder = useOrderHistory((s) => s.remove);
  const location = useLocation();
  const navState = location.state as {
    undoOrderId?: string;
    undoCart?: Cart;
    addPageOrphans?: string[];
  } | null;

  const [reverseLanguage, setReverseLanguage] = useState(false);
  const [activeDish, setActiveDish] = useState<{ dish: Dish; categoryId: string } | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [addingPages, setAddingPages] = useState(false);
  const [undoVisible, setUndoVisible] = useState(false);
  const [undoCtx, setUndoCtx] = useState<{ orderId: string; cart: Cart } | null>(null);
  const [orphanedToast, setOrphanedToast] = useState<string[] | null>(null);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const dismissedBanners = useDismissedAddPageBanners((s) => s.dismissed);
  const dismissBanner = useDismissedAddPageBanners((s) => s.dismiss);
  const setMenuName = useMenuNames((s) => s.setName);

  // If navigation arrived with an order placed, surface the 5-second undo toast.
  useEffect(() => {
    if (navState?.undoOrderId && navState.undoCart) {
      setUndoCtx({ orderId: navState.undoOrderId, cart: navState.undoCart });
      setUndoVisible(true);
      // Drop the state so refresh doesn't re-trigger the toast.
      navigate(location.pathname, { replace: true, state: null });
      const id = setTimeout(() => setUndoVisible(false), 5000);
      return () => clearTimeout(id);
    }
  }, [navState?.undoOrderId, navState?.undoCart, navigate, location.pathname]);

  // After a successful "Add page" re-parse, surface a toast for any cart
  // lines we couldn't migrate (their nameOriginal didn't match a dish in the
  // new menu — typically because Gemini paraphrased between parses).
  useEffect(() => {
    if (navState?.addPageOrphans && navState.addPageOrphans.length > 0) {
      setOrphanedToast(navState.addPageOrphans);
      navigate(location.pathname, { replace: true, state: null });
      const id = setTimeout(() => setOrphanedToast(null), 6000);
      return () => clearTimeout(id);
    }
  }, [navState?.addPageOrphans, navigate, location.pathname]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['menu', menuId],
    enabled: !!menuId,
    queryFn: async () => {
      const local = await loadMenuLocal(menuId!);
      if (local) {
        return { menuId: menuId!, menu: local.menu, modelUsed: local.modelUsed };
      }
      const res = await fetchMenu(menuId!);
      await saveMenuLocal(res.menuId, res.menu, res.modelUsed);
      return res;
    },
  });

  const menu: Menu | undefined = data?.menu;
  const namesMap = useMenuNames((s) => s.names);
  const displayMenuName = resolveMenuName(menuId, menu?.restaurantName, namesMap);
  useDocumentTitle(displayMenuName);

  useEffect(() => {
    if (menuId) setCartMenu(menuId);
  }, [menuId, setCartMenu]);

  const lowConfidence = useMemo(() => (menu ? dishMinConfidence(menu) < 0.55 : false), [menu]);

  // Cuisine-aware coaching: surfaces a heads-up card if the cuisine is known
  // for hidden ingredients relevant to the user's identity (fish sauce in SE
  // Asia for vegan/halal/kosher, lard in Spain for halal/kosher, etc.).
  // Memoized so dismiss state doesn't re-recompute (re-renders pick it up).
  const cuisineHint = useMemo(() => {
    if (!menu) return null;
    const hint = cuisineHintFor(menu.detectedSourceLanguage, onboardingChoice);
    if (!hint || isCuisineHintDismissed(hint.id)) return null;
    return hint;
  }, [menu, onboardingChoice]);

  // Menu-wide confidence — drives both the actionable "retake" strip and the
  // suppression of per-dish ⚠ chips when more than half the menu is flagged
  // (otherwise every card screams uncertain, which is noise).
  const menuConfidence = useMemo(() => {
    if (!menu) return { total: 0, flagged: 0, isLow: false };
    const all = menu.categories.flatMap((c) => c.dishes);
    const flagged = all.filter((d) => d.confidence < DISH_LOW_CONFIDENCE_THRESHOLD).length;
    const ratio = all.length === 0 ? 0 : flagged / all.length;
    return { total: all.length, flagged, isLow: ratio >= MENU_LEVEL_LOW_CONFIDENCE_RATIO };
  }, [menu]);

  if (isLoading || !menu) {
    return (
      <div className="min-h-dvh container-app safe-top safe-bottom flex items-center justify-center">
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh container-app safe-top safe-bottom flex flex-col items-center justify-center gap-4 text-center">
        <p>{(error as Error).message}</p>
        <button onClick={() => navigate('/')} className="btn-primary">{t('error.tryAgain')}</button>
      </div>
    );
  }

  if (addingPages && menuId && menu) {
    return (
      <AddPagesFlow
        previousMenuId={menuId}
        onCancel={() => setAddingPages(false)}
        onSuccess={({ menuId: newMenuId, menu: newMenu }) => {
          const lines = cart?.lines ?? [];
          let orphanedNames: string[] = [];
          if (lines.length > 0) {
            const result = migrateCartToNewMenu(lines, menu, newMenu);
            orphanedNames = result.orphanedNames;
            // Pre-seed the cart at the new menuId so the new MenuScreen's
            // setCartMenu(newMenuId) effect no-ops instead of wiping it.
            restoreCart({ menuId: newMenuId, lines: result.migrated });
          }
          if (namesMap[menuId]) {
            setMenuName(newMenuId, namesMap[menuId]);
          }
          setAddingPages(false);
          navigate(`/menu/${newMenuId}`, {
            replace: true,
            state: orphanedNames.length > 0 ? { addPageOrphans: orphanedNames } : null,
          });
        }}
      />
    );
  }

  const showAddPageBanner = (menu.pageImages ?? []).length === 1 && !dismissedBanners[menuId!];

  const cartCountFor = (dishId: string): number =>
    cart?.lines.find((l) => l.dishId === dishId)?.quantity ?? 0;

  return (
    <div className="min-h-dvh container-app safe-top">
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: 'var(--color-bg)',
          paddingTop: 12,
          paddingBottom: 8,
          marginInline: -20,
          paddingInline: 20,
          borderBottom: '1px solid var(--color-divider)',
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <Link to="/" aria-label="Back" style={{ minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', color: 'var(--color-primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </Link>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            {displayMenuName ? (
              <h1 style={{ fontSize: 16, fontWeight: 600, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayMenuName}
              </h1>
            ) : (
              <Logo size="sm" />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => setReverseLanguage((v) => !v)}
              aria-label={t('menu.toggleLanguage')}
              style={{
                padding: '6px 10px',
                borderRadius: 9999,
                border: '1px solid var(--color-divider)',
                background: 'transparent',
                color: 'var(--color-primary)',
                fontSize: 12,
                fontWeight: 500,
                minHeight: 32,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {(reverseLanguage ? menu.targetLanguage : menu.detectedSourceLanguage).toUpperCase()}
              <span style={{ opacity: 0.4 }}>·</span>
              {(reverseLanguage ? menu.detectedSourceLanguage : menu.targetLanguage).toUpperCase()}
            </button>
            <button
              onClick={() => setActionsOpen(true)}
              aria-label="More actions"
              style={{
                width: 36,
                height: 32,
                borderRadius: 9999,
                border: '1px solid var(--color-divider)',
                background: 'transparent',
                color: 'var(--color-primary)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
              </svg>
            </button>
          </div>
        </div>

        {menu.categories.length > 1 && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              marginTop: 12,
              paddingBottom: 4,
              scrollbarWidth: 'none',
            }}
          >
            {menu.categories.map((c) => {
              const isActive = activeCategory === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => {
                    const el = categoryRefs.current[c.id];
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      setActiveCategory(c.id);
                    }
                  }}
                  style={{
                    flexShrink: 0,
                    padding: '6px 12px',
                    borderRadius: 9999,
                    border: '1px solid var(--color-divider)',
                    background: isActive ? 'var(--color-primary)' : 'transparent',
                    color: isActive ? 'var(--color-bg)' : 'var(--color-text-secondary)',
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {reverseLanguage ? c.nameOriginal : c.nameTranslated}
                </button>
              );
            })}
          </div>
        )}

        {/* Inside the sticky header so it stays visible while the user scrolls
            categories — the failure mode being addressed is "missed a page,
            scrolled away, forgot the affordance existed." */}
        {showAddPageBanner && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px 8px 12px',
              background: 'var(--color-surface)',
              borderRadius: 10,
              borderLeft: '3px solid var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
            }}
          >
            <span style={{ flex: 1, color: 'var(--color-text-secondary)' }}>
              Missed a page? Add a photo to this menu.
            </span>
            <button
              onClick={() => setAddingPages(true)}
              style={{
                padding: '6px 12px',
                borderRadius: 9999,
                border: 'none',
                background: 'var(--color-primary)',
                color: 'var(--color-bg)',
                fontSize: 13,
                fontWeight: 600,
                minHeight: 32,
                whiteSpace: 'nowrap',
              }}
            >
              Add
            </button>
            <button
              onClick={() => menuId && dismissBanner(menuId)}
              aria-label="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-muted)',
                minWidth: 32,
                minHeight: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </header>

      {lowConfidence && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: 'rgba(198, 139, 0, 0.12)',
            color: 'var(--color-warning)',
            border: '1px solid var(--color-warning)',
            borderRadius: 12,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Some dishes may not have been read clearly. Items marked ⚠ are uncertain — confirm with your server.
        </div>
      )}

      {filteredAllergens.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 14px',
            background: 'var(--color-surface)',
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
          }}
        >
          <span>
            Filtering: <strong>{filteredAllergens.join(', ')}</strong>
          </span>
          <button
            onClick={() => setShowRevealed(!showRevealed)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-accent)',
              fontWeight: 500,
              fontSize: 13,
              padding: '4px 6px',
            }}
          >
            {showRevealed ? 'Hide flagged' : t('menu.showAnyway')}
          </button>
        </div>
      )}

      {dietFilter && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setDietFilterActive((v) => !v)}
            aria-pressed={dietFilterActive}
            style={{
              padding: '8px 14px',
              borderRadius: 9999,
              border: `1.5px solid ${dietFilterActive ? 'var(--color-primary)' : 'var(--color-divider)'}`,
              background: dietFilterActive ? 'var(--color-primary)' : 'transparent',
              color: dietFilterActive ? 'var(--color-bg)' : 'var(--color-primary)',
              fontSize: 13,
              fontWeight: 600,
              minHeight: 36,
            }}
          >
            {DIET_FILTER_LABELS[dietFilter]}
          </button>
        </div>
      )}

      <main style={{ paddingBottom: 120 }}>
        {(menu.notes ?? []).length > 0 && (
          <div style={{ paddingTop: 12 }}>
            <NotesBanner notes={menu.notes} dismissKey={menuId} />
          </div>
        )}
        {cuisineHint && <CuisineCoach hint={cuisineHint} />}
        {menuConfidence.isLow && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 10,
              background: 'rgba(196,51,43,0.08)',
              border: '1px solid rgba(196,51,43,0.35)',
              color: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 180, fontSize: 13, lineHeight: 1.4 }}>
              <strong>Translation quality is low.</strong>{' '}
              {menuConfidence.flagged} of {menuConfidence.total} dishes uncertain. Retake flat, well-lit, one page at a time.
            </div>
            <button
              onClick={() => navigate('/scan')}
              style={{
                padding: '8px 16px',
                borderRadius: 9999,
                border: 'none',
                background: '#C4332B',
                color: '#FFFFFF',
                fontSize: 13,
                fontWeight: 600,
                minHeight: 36,
                whiteSpace: 'nowrap',
              }}
            >
              Retake
            </button>
          </div>
        )}
        {(() => {
          const suppressUncertainty = menuConfidence.isLow;
          return menu.categories.map((c) => {
            const visibleDishes = dietFilterActive && dietFilter
              ? c.dishes.filter((d) => isLikelyForDiet(d, dietFilter))
              : c.dishes;
            const hidden = c.dishes.length - visibleDishes.length;
            return (
              <section
                key={c.id}
                ref={(el) => { categoryRefs.current[c.id] = el; }}
                style={{ paddingTop: 24, scrollMarginTop: 110 }}
              >
                <h2 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
                  {reverseLanguage ? c.nameOriginal : c.nameTranslated}
                </h2>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
                  {reverseLanguage ? c.nameTranslated : c.nameOriginal}
                </p>
                {visibleDishes.length === 0 ? (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>
                    {hidden > 0 ? `No ${DIET_FILTER_LABELS[dietFilter!].toLowerCase().replace(' only', '')} dishes here.` : t('menu.empty')}
                  </p>
                ) : (
                  <div>
                    {visibleDishes.map((d) => (
                      <DishCard
                        key={d.id}
                        dish={d}
                        currency={menu.currency}
                        filteredAllergens={filteredAllergens}
                        hideUncertain={hideUncertainAllergens}
                        showFiltered={showRevealed}
                        reverseLanguage={reverseLanguage}
                        inCart={cartCountFor(d.id)}
                        suppressUncertainty={suppressUncertainty}
                        onTap={() => setActiveDish({ dish: d, categoryId: c.id })}
                        onQuickAdd={() => addLine({ dishId: d.id, categoryId: c.id, quantity: 1, modifications: '' })}
                      />
                    ))}
                    {hidden > 0 && (
                      <p style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 8, fontStyle: 'italic' }}>
                        {hidden} hidden by diet filter
                      </p>
                    )}
                  </div>
                )}
              </section>
            );
          });
        })()}
      </main>

      <CartPill menu={menu} onClick={() => navigate(`/menu/${menuId}/order`)} />

      {photoViewerOpen && (menu.pageImages ?? []).length > 0 && (
        <MenuPhotoViewer
          pages={(menu.pageImages ?? []).map((raw, i) => resolvePageSrc(menuId!, raw, i))}
          onClose={() => setPhotoViewerOpen(false)}
        />
      )}

      {menuId && (
        <MenuActionsSheet
          open={actionsOpen}
          onClose={() => setActionsOpen(false)}
          menuId={menuId}
          restaurantName={displayMenuName}
          detectedName={menu.restaurantName ?? null}
          hasOriginalPhoto={(menu.pageImages ?? []).length > 0}
          onViewOriginal={() => setPhotoViewerOpen(true)}
        />
      )}

      {activeDish && (() => {
        const existing = cart?.lines.find((l) => l.dishId === activeDish.dish.id);
        // Only enforce kosher meat+dairy separation for users who picked the
        // kosher identity. Other users add freely.
        const mealMode = onboardingChoice === 'kosher'
          ? deriveMealMode(menu, cart && { ...cart, lines: cart.lines.filter((l) => l.dishId !== activeDish.dish.id) })
          : null;
        return (
          <DishSheet
            dish={activeDish.dish}
            currency={menu.currency}
            sourceLanguage={menu.detectedSourceLanguage}
            menuId={menuId}
            existingMealMode={mealMode}
            initialQuantity={existing?.quantity ?? 1}
            initialModifications={existing?.modifications ?? ''}
            initialDietaryTags={existing?.dietaryTags ?? []}
            initialOverridden={existing?.overridden ?? false}
            onConfirm={({ quantity, modifications, dietaryTags, overridden }) => {
              upsertLine({
                dishId: activeDish.dish.id,
                categoryId: activeDish.categoryId,
                quantity,
                modifications,
                dietaryTags: dietaryTags.length > 0 ? dietaryTags : undefined,
                overridden: overridden || undefined,
              });
              setActiveDish(null);
            }}
            onClose={() => setActiveDish(null)}
          />
        );
      })()}

      {orphanedToast && orphanedToast.length > 0 && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100vw - 32px)',
            background: '#0A0A0A',
            color: '#FFFFFF',
            borderRadius: 14,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            zIndex: 40,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <span style={{ flex: 1 }}>
            Couldn't carry over: {orphanedToast.join(', ')}
          </span>
          <button
            onClick={() => setOrphanedToast(null)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              color: '#FFB347',
              border: 'none',
              padding: '2px 6px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      )}

      {undoVisible && undoCtx && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 88px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#0A0A0A',
            color: '#FFFFFF',
            borderRadius: 9999,
            padding: '10px 14px 10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 40,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            fontSize: 14,
          }}
        >
          <span>{t('undo.placed')}</span>
          <button
            onClick={() => {
              if (!undoCtx) return;
              removeOrder(undoCtx.orderId);
              restoreCart(undoCtx.cart);
              setUndoVisible(false);
              // Best-effort delete on the server too. Ignore failures.
              void deleteOrderRemote(undoCtx.orderId).catch(() => {});
            }}
            style={{
              background: 'transparent',
              color: '#FFB347',
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: 9999,
              cursor: 'pointer',
            }}
          >
            {t('undo.label')}
          </button>
        </div>
      )}
    </div>
  );
}
